import CardSystem from '../../Card/CardSystem';
import { UIEvent } from 'System/UI/BaseUISystem';
import {
    AwaitIterators,
    PlayAnimation,
    StartAnimationClip,
    StartAnimationClipAsync,
} from 'System/Animation/AnimationHelper';
import { Easing, Interpolation } from '@tweenjs/tween.js';
import TransformComponent from 'Component/Transform';
import { MainAnim } from 'System/Animation/Clip/MainAnim';
import { DragonbonesDefault, DragonbonesFadeIn } from 'System/Animation/Clip';
import Point from 'Utility/Point';
import AnimationClip from 'System/Animation/AnimationClip';
import BaseCard from 'Prototype/Level/Card/BaseCard';
import { delay } from 'Utility/Task/TaskQueue';
import Renderable from 'Component/Renderable';
import Interactable from 'Component/Interactable';
import CardComponent from 'Component/Level/Card';
import Animator from 'Prototype/Animator';
import { MathHelper } from 'Utility/MathHelper';
import Armature from '../../../Prototype/Armature';
import { sleep } from '../../../Utility/async';

interface IReceiveJokerPreBoosterMeta {
    system: CardSystem;
    cards: BaseCard[];
    jokers: BaseCard[];
}
const maxCardSwayAngle = 15;
export class RevealJokerPreBooster extends AnimationClip<IReceiveJokerPreBoosterMeta> {
    public *execute(animator: Animator, meta: IReceiveJokerPreBoosterMeta): IterableIterator<number> {
        const { system, cards, jokers } = meta;
        const delayInterval: number = 250;
        system.scene.emit(UIEvent.DISABLE_INTERACTIVITY, false);

        const animatorTransform = animator.getComponent(TransformComponent);

        const center = new Point(animatorTransform.width / 2, animatorTransform.height / 2);

        const cardsState = system.store.getState().levelState.cardsState;
        yield* AwaitIterators(jokers.map((joker, index) => placeJoker(joker, index)));

        system.scene.emit(UIEvent.RESTORE_INTERACTIVITY, true);
        return;

        function* placeJoker(joker: BaseCard, index: number) {
            const assignedCard = cards[index];
            const armature = joker.getArmature();
            const jokerTransform = joker.getComponent(TransformComponent);
            const jokerCardComponent = joker.getComponent(CardComponent);
            const cardTransform = assignedCard.getComponent(TransformComponent);
            const side = index === 0 ? 0 : (index % 2) * 2 - 1;
            const distanceFromCenter = side * (300 * Math.ceil(index / cards.length));
            const invertedDelayInterval = (jokers.length - 1 - index) * delayInterval;
            animator.addChild(joker);
            jokerTransform.position.set(distanceFromCenter + center.x, center.y);
            jokerTransform.rotation = 0;
            const assignedCardDistance = cardTransform.x - jokerTransform.x;
            joker.getComponent(Renderable).visible = true;
            joker.getComponent(Interactable).enabled = false;
            jokerCardComponent.animating = true;
            jokerTransform.scale.set(1.8);

            yield* delay(index * delayInterval);
            yield* PlayAnimation(
                new DragonbonesFadeIn(armature, {
                    playTimes: 1,
                    animationName: 'start',
                    concat: false,
                })
            );
            playIdle(armature);

            assignedCard.parent.addChild(joker);

            const swayAngle =
                Math.sign(assignedCardDistance) *
                MathHelper.remap(Math.abs(assignedCardDistance), 0, center.x, 0, maxCardSwayAngle);
            yield* AwaitIterators([
                swayCard(joker, swayAngle),
                PlayAnimation(
                    new MainAnim(
                        joker,
                        {
                            x: [jokerTransform.x + assignedCardDistance / 2, jokerCardComponent.x],
                            y: [cardTransform.y - 500, jokerCardComponent.y - 155],
                            scaleX: 1,
                            scaleY: 1,
                            duration: 625 + invertedDelayInterval,
                            rotation: cardTransform.rotation,
                            easing: Easing.Sinusoidal.Out,
                            interpolation: Interpolation.Bezier,
                        },
                        true
                    )
                ),
            ]);

            system.sortCardsInPlay(cardsState);

            yield* PlayAnimation(
                new MainAnim(
                    joker,
                    {
                        y: jokerCardComponent.y,
                        duration: 325,
                    },
                    true
                )
            );

            jokerCardComponent.animating = false;
        }

        function* swayCard(card: BaseCard, angle: number): IterableIterator<number> {
            yield* PlayAnimation(
                new MainAnim(
                    card,
                    {
                        angle: 15,
                        duration: 250,
                    },
                    true
                )
            );
            yield* PlayAnimation(
                new MainAnim(
                    card,
                    {
                        angle: 0,
                        duration: 250,
                    },
                    true
                )
            );
        }

        async function playIdle(armature: Armature): Promise<void> {
            await sleep(5000 + Math.random() * 5000);

            await StartAnimationClipAsync(
                new DragonbonesDefault(armature, {
                    animationName: 'idle',
                    playTimes: 1,
                })
            );

            playIdle(armature);
        }
    }
}
